import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { SITE_INFO } from '../data';
import { getSiteSettings } from '../lib/cmsClient';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [siteSettings, setSiteSettings] = useState({});
  const location = useLocation();
  const dropdownRef = useRef(null);

  // Track scroll position for compact sticky header
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    function handleScroll() {
      setIsScrolled(window.scrollY > 20);
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    async function loadSettings() {
      try {
        const settings = await getSiteSettings();
        if (settings) {
          setSiteSettings(settings);
        }
      } catch (err) {
        console.warn("Could not load header settings from CMS:", err);
      }
    }
    loadSettings();
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Primary navigation: "På pladsen" is a direct link with no dropdown
  const navTree = [
    { label: 'Forside', href: '/' },
    { label: 'Entreprenør og anlæg', href: '/entreprenerservice' },
    {
      label: 'Kloak og klimasikring',
      href: '/kloak-service',
      children: [
        { label: 'Kloak og klimasikring (Oversigt)', href: '/kloak-service' },
        { label: 'TV-inspektion og kloakspuling', href: '/tv-inspektion' },
        { label: 'Rottespærre', href: '/rottespaerre' },
        { label: 'Klimasikring og skybrud', href: '/klimatilpasning' },
        { label: 'Minirenseanlæg', href: '/minirensningsanlaeg' }
      ]
    },
    {
      label: 'Betonarbejde',
      href: '/betonarbejde',
      children: [
        { label: 'Betonarbejde (Oversigt)', href: '/betonarbejde' },
        { label: 'Statens Museum for Kunst (SMK)', href: '/smk' }
      ]
    },
    { label: 'På pladsen', href: '/bygningsaffald' },
    { label: 'Udlejning', href: '/udlejning' },
    { label: 'Om os', href: '/om-os' },
    { label: 'Kontakt', href: '/kontakt' }
  ];

  const isChildActive = (children) => {
    if (!children) return false;
    return children.some((child) => location.pathname === child.href || decodeURIComponent(location.pathname) === decodeURIComponent(child.href));
  };

  const logoUrl = siteSettings.logo?.image || "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/imgi-8-slide1-edited.jpg";

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 bg-white border-b border-stone-200 shadow-sm text-stone-800 ${
      isScrolled ? 'py-1 shadow-md' : 'py-2 sm:py-3'
    }`}>
      {/* Top utility bar & logo row */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center group focus:outline-none p-0.5"
            aria-label="KTS-Entreprise A/S Forside"
          >
            <img
              src={`https://assets.auxodev.dk/cdn-cgi/image/width=500,format=auto,quality=95/${logoUrl.replace(/^https:\/\/assets\.auxodev\.dk\//, '')}`}
              alt="KTS-Entreprise A/S"
              className={`w-auto object-contain transition-all duration-200 ${
                isScrolled ? 'h-10 sm:h-12' : 'h-12 sm:h-14 lg:h-16'
              }`}
            />
          </Link>

          {/* Desktop Right Info & CTA */}
          <div className="hidden lg:flex items-center gap-6">
            <div className="text-right hidden xl:block">
              <span className="block text-[11px] uppercase tracking-wider text-stone-500 font-heading font-semibold">
                Direkte kontakt &amp; rådgivning
              </span>
              <a
                href={`tel:${SITE_INFO.mainPhone.replace(/\s+/g, '')}`}
                className="text-stone-900 hover:text-[#0072c0] font-bold text-base tracking-tight font-mono transition-colors flex items-center justify-end gap-1.5"
              >
                <i className="fa-solid fa-phone text-[#0072c0] text-xs" aria-hidden="true"></i>
                <span>Tlf. {SITE_INFO.mainPhone}</span>
              </a>
            </div>

            <div className="flex items-center gap-3">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 bg-[#0072c0] hover:bg-[#005b99] text-white font-heading font-bold uppercase tracking-wider px-5 py-2.5 text-xs sm:text-sm transition-all duration-150 shadow-xs cursor-pointer rounded-xs"
              >
                <span>Få et tilbud</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
              </Link>
            </div>
          </div>

          {/* Mobile Right Controls: Phone + Hamburger Button */}
          <div className="flex items-center gap-2 lg:hidden">
            <a
              href={`tel:${SITE_INFO.mainPhone.replace(/\s+/g, '')}`}
              className="inline-flex items-center justify-center w-9 h-9 bg-stone-100 text-[#0072c0] border border-stone-300 rounded-xs"
              aria-label="Ring til os"
            >
              <i className="fa-solid fa-phone text-sm" aria-hidden="true"></i>
            </a>

            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-xs text-stone-800 hover:text-stone-950 hover:bg-stone-100 border border-stone-300 transition-colors focus:outline-none"
              aria-expanded={mobileMenuOpen}
              aria-label="Åbn menu"
            >
              <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-xl`} aria-hidden="true"></i>
            </button>
          </div>

        </div>

        {/* Desktop Primary Navigation Bar */}
        <div className="hidden lg:block border-t border-stone-200 mt-2 pt-2" ref={dropdownRef}>
          <nav className="flex items-center justify-between" aria-label="Hovednavigation">
            <ul className="flex items-center gap-1 xl:gap-2 flex-wrap">
              {navTree.map((item) => {
                const hasChildren = item.children && item.children.length > 0;
                const isActive = location.pathname === item.href || (hasChildren && isChildActive(item.children));
                const isCurrentDropdownOpen = openDropdown === item.label;

                return (
                  <li key={item.label} className="relative">
                    {hasChildren ? (
                      <div className="relative group">
                        <button
                          type="button"
                          onClick={() => setOpenDropdown(isCurrentDropdownOpen ? null : item.label)}
                          className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs xl:text-sm font-heading uppercase font-bold tracking-wider transition-colors ${
                            isActive
                              ? 'text-[#0072c0] border-b-2 border-[#0072c0] pb-1.5'
                              : 'text-stone-700 hover:text-[#0072c0]'
                          }`}
                          aria-expanded={isCurrentDropdownOpen}
                        >
                          <span>{item.label}</span>
                          <i className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${
                            isCurrentDropdownOpen ? 'rotate-180 text-[#0072c0]' : 'text-stone-400'
                          }`} aria-hidden="true"></i>
                        </button>

                        {/* Dropdown Menu */}
                        {isCurrentDropdownOpen && (
                          <div className="absolute left-0 top-full mt-1 w-64 bg-white border border-stone-200 shadow-lg py-2 z-50 rounded-xs">
                            {item.children.map((child) => (
                              <NavLink
                                key={child.href}
                                to={child.href}
                                onClick={() => setOpenDropdown(null)}
                                className={({ isActive: childActive }) =>
                                   `block px-4 py-2.5 text-xs font-heading font-bold uppercase tracking-wider transition-colors ${
                                    childActive
                                      ? 'bg-stone-100 text-[#0072c0] border-l-2 border-[#0072c0]'
                                      : 'text-stone-700 hover:bg-stone-50 hover:text-[#0072c0]'
                                  }`
                                }
                              >
                                {child.label}
                              </NavLink>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <NavLink
                        to={item.href}
                        className={({ isActive: directActive }) =>
                          `inline-block px-3 py-2 text-xs xl:text-sm font-heading uppercase font-bold tracking-wider transition-colors ${
                            directActive
                              ? 'text-[#0072c0] border-b-2 border-[#0072c0] pb-1.5'
                              : 'text-stone-700 hover:text-[#0072c0]'
                          }`
                        }
                      >
                        {item.label}
                      </NavLink>
                    )}
                  </li>
                );
              })}
            </ul>

          </nav>
        </div>
      </div>

      {/* Mobile Off-Canvas Dropdown Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-b border-stone-200 max-h-[85vh] overflow-y-auto px-4 py-6 shadow-xl">
          <nav className="space-y-2" aria-label="Mobil navigation">
            {navTree.map((item) => {
              const hasChildren = item.children && item.children.length > 0;
              const isCurrentOpen = openDropdown === item.label;

              return (
                <div key={item.label} className="border-b border-stone-100 pb-2">
                  {hasChildren ? (
                    <div>
                      <button
                        type="button"
                        onClick={() => setOpenDropdown(isCurrentOpen ? null : item.label)}
                        className="w-full flex items-center justify-between py-2 text-sm font-heading uppercase font-bold text-stone-900 tracking-wider"
                      >
                        <span>{item.label}</span>
                        <i className={`fa-solid ${isCurrentOpen ? 'fa-chevron-up' : 'fa-chevron-down'} text-stone-500 text-xs`} aria-hidden="true"></i>
                      </button>
                      {isCurrentOpen && (
                        <div className="pl-4 space-y-1.5 py-2 bg-stone-50 rounded-xs mb-2">
                          {item.children.map((child) => (
                            <Link
                              key={child.href}
                              to={child.href}
                              onClick={() => setMobileMenuOpen(false)}
                              className="block py-2 text-xs font-heading font-semibold uppercase text-stone-700 hover:text-[#0072c0]"
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="block py-2 text-sm font-heading uppercase font-bold text-stone-900 hover:text-[#0072c0] tracking-wider"
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              );
            })}

            {/* Mobile Contact & CTA */}
            <div className="pt-4 space-y-3">
              <a
                href={`tel:${SITE_INFO.mainPhone.replace(/\s+/g, '')}`}
                className="w-full inline-flex items-center justify-center gap-2 bg-stone-100 text-stone-900 font-heading font-bold uppercase tracking-wider py-3 text-xs border border-stone-300 rounded-xs"
              >
                <i className="fa-solid fa-phone text-[#0072c0]" aria-hidden="true"></i>
                <span>Ring {SITE_INFO.mainPhone}</span>
              </a>

              <Link
                to="/kontakt"
                onClick={() => setMobileMenuOpen(false)}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#0072c0] hover:bg-[#005b99] text-white font-heading font-bold uppercase tracking-wider py-3 text-xs rounded-xs shadow-xs"
              >
                <span>Få et uforpligtende tilbud</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
